const unwrapJsonValue = (value, depth = 0) => {
  if (depth > 4 || value === null || value === undefined) return value;
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const looksLikeJson =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    trimmed.startsWith('"');
  if (!looksLikeJson) return value;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed === value) return value;
    return unwrapJsonValue(parsed, depth + 1);
  } catch {
    return value;
  }
};

export const formatForPreBlock = (value) => {
  if (value === null || value === undefined) return '';
  const unwrapped = unwrapJsonValue(value);
  if (unwrapped === null || unwrapped === undefined) return '';
  if (typeof unwrapped === 'object') {
    try {
      return JSON.stringify(unwrapped, null, 2);
    } catch {
      return String(unwrapped);
    }
  }
  return String(unwrapped);
};

export const oneLinePreview = (value, maxLen = 100) => {
  if (value === null || value === undefined || value === '') return '';
  const raw = typeof value === 'string' ? value : formatForPreBlock(value);
  const single = raw.replace(/\s+/g, ' ').trim();
  if (!single) return '';
  return single.length > maxLen ? `${single.slice(0, maxLen)}…` : single;
};

export const getLogAgentResponse = (log) => {
  const candidates = [
    log?.output,
    log?.agent_response,
    log?.agent_output,
    log?.response,
    log?.response_body,
    log?.result,
    log?.result?.response,
    log?.result?.output,
    log?.execution_result?.response,
    log?.execution_result?.output,
    log?.payload?.response,
    log?.payload?.output,
  ];
  return candidates.find((value) => value !== null && value !== undefined && value !== '');
};

export const getLogTimeValue = (log) =>
  log?.completed_at || log?.started_at || log?.created_at || log?.timestamp;

const parseLogDateTimeValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    return new Date(ms);
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    const num = Number(trimmed);
    const ms = num < 1e12 ? num * 1000 : num;
    return new Date(ms);
  }

  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  if (hasTimezone) {
    return new Date(trimmed);
  }

  // Timezone-less API timestamps are local wall-clock values (schedule/trigger logs).
  const normalized = trimmed.replace(' ', 'T');
  const localMatch = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/,
  );
  if (localMatch) {
    const [, year, month, day, hour, minute, second = '0', fraction = '0'] = localMatch;
    const ms = Number(String(fraction).slice(0, 3).padEnd(3, '0')) || 0;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      ms,
    );
  }

  return new Date(normalized);
};

export const formatLogDateTime = (value) => {
  const date = parseLogDateTimeValue(value);
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

export const getLogDateBucket = (value) => {
  if (!value) return 'older';
  const date = parseLogDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return 'older';
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startToday - startDate) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  return 'older';
};

export const getLogDateLabel = (bucket, value) => {
  if (bucket === 'today') return 'Today';
  if (bucket === 'yesterday') return 'Yesterday';
  if (!value) return 'Older';
  return parseLogDateTimeValue(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

export const getLocalDateKey = (value) => {
  if (!value) return '';
  const date = parseLogDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const getLogStatusMeta = (status) => {
  const normalized = String(status || 'unknown').toLowerCase();
  if (['success', 'completed', 'complete', 'done'].includes(normalized)) {
    return { label: 'Completed', tone: 'completed' };
  }
  if (['running', 'in_progress', 'processing', 'queued', 'pending'].includes(normalized)) {
    return { label: 'Running', tone: 'running' };
  }
  if (['failed', 'failure', 'error'].includes(normalized)) {
    return { label: 'Failed', tone: 'failed' };
  }
  if (normalized === 'cancelled' || normalized === 'canceled') {
    return { label: 'Cancelled', tone: 'inactive' };
  }
  return {
    label: normalized.charAt(0).toUpperCase() + normalized.slice(1),
    tone: 'inactive',
  };
};

export const getWebhookDeliveryStatusMeta = (responseStatus) => {
  const code = Number(responseStatus);
  if (Number.isFinite(code) && code >= 200 && code < 300) {
    return { label: String(code), tone: 'completed' };
  }
  return { label: String(responseStatus || 'ERR'), tone: 'failed' };
};

export const filterLogsByDate = (logs, logDateFilter, customLogDate) => {
  if (logDateFilter === 'all') return logs;
  if (logDateFilter === 'custom') {
    if (!customLogDate) return logs;
    return logs.filter((log) => getLocalDateKey(getLogTimeValue(log)) === customLogDate);
  }
  return logs.filter((log) => getLogDateBucket(getLogTimeValue(log)) === logDateFilter);
};

export const groupLogsByDate = (logs) => {
  const groups = [];
  logs.forEach((log) => {
    const dateValue = getLogTimeValue(log);
    const bucket = getLogDateBucket(dateValue);
    const label = getLogDateLabel(bucket, dateValue);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.label === label) lastGroup.items.push(log);
    else groups.push({ label, items: [log] });
  });
  return groups;
};

export const buildAgentExecutionSections = (log) => {
  const sections = [];
  const agentResponse = getLogAgentResponse(log);

  if (log?.input_query !== null && log?.input_query !== undefined && log?.input_query !== '') {
    sections.push({ key: 'query', title: 'Request', value: log.input_query });
  }
  if (log?.input_payload !== null && log?.input_payload !== undefined && log?.input_payload !== '') {
    sections.push({ key: 'payload', title: 'Payload', value: log.input_payload });
  }
  sections.push({
    key: 'output',
    title: 'Response',
    value: agentResponse,
    emptyLabel: 'No output',
  });
  return sections;
};

export const buildWebhookDeliverySections = (log) => {
  const sections = [];
  if (log?.payload !== null && log?.payload !== undefined && log?.payload !== '') {
    sections.push({ key: 'payload', title: 'Payload', value: log.payload });
  }
  if (log?.response_body !== null && log?.response_body !== undefined && log?.response_body !== '') {
    sections.push({ key: 'response', title: 'Response', value: log.response_body });
  }
  if (log?.error_message) {
    sections.push({ key: 'error', title: 'Error', value: log.error_message });
  }
  return sections;
};

export const LOG_DATE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'custom', label: 'Custom' },
];
